import type { OnboardingStep, OnboardingTour } from '../types';
import { getUtilizationTourValues, utilizationSetupDish, utilizationQuantityCompleted, utilizationFavoritesCompleted } from '../utilizationValues';
import { setUtilizationExerciseSource } from '../practice/utilizationPractice';
import { inputSettled } from '../advanceHelpers';
import { findOnboardingTarget } from '../findTarget';
import {
  closeUtilizationEdit,
  fillUtilizationEditQty,
  showUtilizationFavoritePick,
  showUtilizationFavoriteTab,
  fillUtilizationIngredientQty,
  openUtilizationAddLink,
  pointUtilizationUnmappedTab,
  searchUtilizationSetupDish,
  selectUtilizationSetupDish,
  showUtilizationAddLink,
  showUtilizationBreakdown,
  showUtilizationComplete,
  showUtilizationEditDish,
  showUtilizationEditIngredient,
  showUtilizationFavoritesTab,
  showUtilizationLinked,
  showUtilizationOverTab,
  showUtilizationOverview,
  showUtilizationPage,
  showUtilizationProduct,
  showUtilizationProductAdded,
  showUtilizationSetupCta,
  showUtilizationSetupRevenue,
  showUtilizationTabs,
  showUtilizationUnmappedTab,
} from '../utilizationActions';

const route = '/utilization';
const next = 'onboarding.next';
const skip = 'onboarding.skip';
const searchExercise = inputSettled('inventory-setup-search', { minLength: 3 });

export const utilizationSteps: OnboardingStep[] = [
  {
    id: 'utilization-welcome',
    route,
    layout: 'hero',
    illustration: 'welcomeUtilization',
    titleKey: 'onboarding.utilization.welcome.title',
    descriptionKey: 'onboarding.utilization.welcome.description',
    primaryLabelKey: 'onboarding.utilization.welcome.cta',
    secondaryLabelKey: skip,
    onEnter: showUtilizationPage,
  },
  {
    id: 'utilization-improve',
    route,
    target: 'utilization-kpis',
    arrowTarget: 'utilization-tab-over',
    layout: 'hero',
    illustration: 'utilizationTasks',
    titleKey: 'onboarding.utilization.improve.title',
    descriptionKey: 'onboarding.utilization.improve.description',
    values: getUtilizationTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'below',
    separateHoles: true,
    padding: 4,
    radius: 12,
    onEnter: showUtilizationOverTab,
  },
  {
    id: 'utilization-tabs',
    route,
    target: 'utilization-tabs',
    layout: 'compact',
    illustration: 'windows',
    titleKey: 'onboarding.utilization.tabs.title',
    descriptionKey: 'onboarding.utilization.tabs.description',
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'below',
    highlightTarget: true,
    padding: 8,
    radius: 12,
    onEnter: showUtilizationTabs,
  },
  {
    id: 'utilization-over',
    route,
    target: 'utilization-tab-over',
    layout: 'compact',
    illustration: 'click',
    titleKey: 'onboarding.utilization.over.title',
    descriptionKey: 'onboarding.utilization.over.description',
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'below',
    highlightTarget: true,
    allowTargetInteraction: true,
    advanceOnTargetClick: true,
    padding: 8,
    radius: 12,
    onEnter: showUtilizationTabs,
  },
  {
    id: 'utilization-dish-filter',
    route,
    target: 'utilization-table',
    layout: 'compact',
    illustration: 'target',
    titleKey: 'onboarding.utilization.dishFilter.title',
    descriptionKey: 'onboarding.utilization.dishFilter.description',
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'above',
    padding: 8,
    radius: 12,
    onEnter: showUtilizationOverTab,
  },
  {
    id: 'utilization-open-product',
    route,
    target: 'utilization-row',
    layout: 'compact',
    illustration: 'click',
    titleKey: 'onboarding.utilization.openProduct.title',
    descriptionKey: 'onboarding.utilization.openProduct.description',
    values: getUtilizationTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    highlightTarget: true,
    allowTargetInteraction: true,
    advanceOnTargetClick: true,
    padding: 4,
    radius: 12,
    onEnter: showUtilizationProduct,
  },
  {
    id: 'utilization-overview',
    target: 'utilization-drawer',
    arrowTarget: 'utilization-overview',
    layout: 'compact',
    illustration: 'costAb',
    titleKey: 'onboarding.utilization.overview.title',
    descriptionKey: 'onboarding.utilization.overview.description',
    values: getUtilizationTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    padding: 0,
    radius: 12,
    onEnter: showUtilizationOverview,
  },
  {
    id: 'utilization-breakdown',
    target: 'utilization-drawer',
    arrowTarget: 'utilization-breakdown',
    layout: 'compact',
    illustration: 'costAb',
    titleKey: 'onboarding.utilization.breakdown.title',
    descriptionKey: 'onboarding.utilization.breakdown.description',
    values: getUtilizationTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    padding: 0,
    radius: 12,
    onEnter: showUtilizationBreakdown,
  },
  {
    id: 'utilization-add-link',
    target: 'utilization-drawer',
    arrowTarget: 'utilization-add-link',
    layout: 'compact',
    illustration: 'click',
    titleKey: 'onboarding.utilization.addLink.title',
    descriptionKey: 'onboarding.utilization.addLink.description',
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    highlightTarget: true,
    allowTargetInteraction: true,
    advanceOnTargetClick: true,
    padding: 0,
    radius: 12,
    onEnter: showUtilizationAddLink,
  },
  {
    id: 'utilization-setup-type',
    target: 'inventory-setup',
    arrowTarget: 'inventory-setup-tab-revenue',
    layout: 'compact',
    illustration: 'settings2',
    titleKey: 'onboarding.utilization.setupType.title',
    descriptionKey: 'onboarding.utilization.setupType.description',
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    padding: 0,
    radius: 12,
    onEnter: openUtilizationAddLink,
  },
  {
    id: 'utilization-setup-search',
    target: 'inventory-setup',
    arrowTarget: 'inventory-setup-search',
    layout: 'compact',
    illustration: 'costFind',
    titleKey: 'onboarding.utilization.setupSearch.title',
    descriptionKey: 'onboarding.utilization.setupSearch.description',
    values: getUtilizationTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    padding: 0,
    radius: 12,
    allowTargetInteraction: true,
    highlightTarget: true,
    advanceWhen: () => {
      const query = (findOnboardingTarget('inventory-setup-search') as HTMLInputElement | null)?.value.trim().toLocaleLowerCase() ?? '';
      return searchExercise.advanceWhen() && query.length >= 3 && utilizationSetupDish().toLocaleLowerCase().includes(query);
    },
    onEnter: () => {
      const cleanup = showUtilizationSetupRevenue();
      const stopSearch = searchExercise.onEnter();
      return () => { cleanup(); stopSearch?.(); };
    },
  },
  {
    id: 'utilization-setup-pick',
    target: 'inventory-setup',
    arrowTarget: 'inventory-setup-pick',
    layout: 'compact',
    illustration: 'click',
    titleKey: 'onboarding.utilization.setupPick.title',
    descriptionKey: 'onboarding.utilization.setupPick.description',
    values: getUtilizationTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    highlightTarget: true,
    allowTargetInteraction: true,
    advanceOnTargetClick: true,
    padding: 0,
    radius: 12,
    onEnter: searchUtilizationSetupDish,
  },
  {
    id: 'utilization-setup-continue',
    target: 'inventory-setup',
    arrowTarget: 'inventory-setup-continue',
    layout: 'compact',
    illustration: 'click',
    titleKey: 'onboarding.utilization.setupContinue.title',
    descriptionKey: 'onboarding.utilization.setupContinue.description',
    values: getUtilizationTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    highlightTarget: true,
    allowTargetInteraction: true,
    advanceOnTargetClick: true,
    padding: 0,
    radius: 12,
    onEnter: selectUtilizationSetupDish,
  },
  {
    id: 'utilization-product-added',
    target: 'inventory-setup',
    arrowTarget: 'utilization-added-ingredient',
    layout: 'compact',
    illustration: 'check',
    titleKey: 'onboarding.utilization.productAdded.title',
    descriptionKey: 'onboarding.utilization.productAdded.description',
    values: getUtilizationTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    padding: 0,
    radius: 12,
    onEnter: showUtilizationProductAdded,
  },
  {
    id: 'utilization-edit-qty',
    target: 'inventory-setup',
    arrowTarget: 'inventory-leakage-ing-qty',
    layout: 'compact',
    illustration: 'pencil',
    titleKey: 'onboarding.utilization.editQty.title',
    descriptionKey: 'onboarding.utilization.editQty.description',
    values: getUtilizationTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    highlightTarget: true,
    allowTargetInteraction: true,
    padding: 0,
    radius: 12,
    advanceWhen: () => utilizationQuantityCompleted('setup'),
    onEnter: fillUtilizationIngredientQty,
  },
  {
    id: 'utilization-setup-done',
    target: 'inventory-setup',
    arrowTarget: 'inventory-setup-done',
    layout: 'compact',
    illustration: 'boxes',
    titleKey: 'onboarding.utilization.setupDone.title',
    descriptionKey: 'onboarding.utilization.setupDone.description',
    values: getUtilizationTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    highlightTarget: true,
    allowTargetInteraction: true,
    advanceOnTargetClick: true,
    padding: 0,
    radius: 12,
    onEnter: fillUtilizationIngredientQty,
  },
  {
    id: 'utilization-linked',
    target: 'utilization-drawer',
    arrowTarget: 'utilization-breakdown',
    layout: 'compact',
    illustration: 'check',
    titleKey: 'onboarding.utilization.linked.title',
    descriptionKey: 'onboarding.utilization.linked.description',
    values: getUtilizationTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    padding: 0,
    radius: 12,
    onEnter: showUtilizationLinked,
  },
  {
    id: 'utilization-edit-existing',
    target: 'utilization-drawer',
    arrowTarget: 'utilization-edit-dish',
    layout: 'compact',
    illustration: 'click',
    titleKey: 'onboarding.utilization.editExisting.title',
    descriptionKey: 'onboarding.utilization.editExisting.description',
    values: getUtilizationTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    highlightTarget: true,
    allowTargetInteraction: true,
    advanceOnTargetClick: true,
    padding: 0,
    radius: 12,
    onEnter: showUtilizationEditDish,
  },
  {
    id: 'utilization-edit-ingredient',
    target: 'cost-detail-panel',
    arrowTarget: 'utilization-added-ingredient',
    layout: 'compact',
    illustration: 'fruitBasket',
    titleKey: 'onboarding.utilization.editIngredient.title',
    descriptionKey: 'onboarding.utilization.editIngredient.description',
    values: getUtilizationTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    highlightTarget: true,
    padding: 4,
    radius: 12,
    onEnter: showUtilizationEditIngredient,
  },
  {
    id: 'utilization-edit-existing-qty',
    target: 'cost-detail-panel',
    arrowTarget: 'inventory-leakage-ing-qty',
    layout: 'compact',
    illustration: 'boxes',
    titleKey: 'onboarding.utilization.editExistingQty.title',
    descriptionKey: 'onboarding.utilization.editExistingQty.description',
    values: getUtilizationTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    highlightTarget: true,
    allowTargetInteraction: true,
    padding: 0,
    radius: 12,
    advanceWhen: () => utilizationQuantityCompleted('edit'),
    onEnter: fillUtilizationEditQty,
  },
  {
    id: 'utilization-details-done',
    route,
    target: 'utilization-table',
    layout: 'spotlight',
    illustration: 'file',
    titleKey: 'onboarding.utilization.detailsDone.title',
    descriptionKey: 'onboarding.utilization.detailsDone.description',
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'above',
    padding: 8,
    radius: 12,
    onEnter: closeUtilizationEdit,
  },
  {
    id: 'utilization-favorite-pick',
    route,
    target: 'utilization-table',
    arrowTarget: 'utilization-favorite',
    layout: 'compact',
    illustration: 'orangeStar',
    titleKey: 'onboarding.utilization.favoritePick.title',
    descriptionKey: 'onboarding.utilization.favoritePick.description',
    values: getUtilizationTourValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'above',
    highlightTarget: true,
    allowTargetInteraction: true,
    padding: 8,
    radius: 12,
    advanceWhen: utilizationFavoritesCompleted,
    onEnter: showUtilizationFavoritePick,
  },
  {
    id: 'utilization-favorite-tab',
    route,
    target: 'utilization-tab-favorites',
    layout: 'compact',
    illustration: 'click',
    titleKey: 'onboarding.utilization.favoriteTab.title',
    descriptionKey: 'onboarding.utilization.favoriteTab.description',
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'above',
    highlightTarget: true,
    allowTargetInteraction: true,
    advanceOnTargetClick: true,
    padding: 8,
    radius: 12,
    onEnter: showUtilizationFavoriteTab,
  },
  {
    id: 'utilization-favorites-focus',
    route,
    target: 'utilization-table',
    layout: 'compact',
    illustration: 'orangeStar',
    titleKey: 'onboarding.utilization.favoritesFocus.title',
    descriptionKey: 'onboarding.utilization.favoritesFocus.description',
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    padding: 8,
    radius: 12,
    onEnter: showUtilizationFavoritesTab,
  },
  {
    id: 'utilization-unmapped-tab',
    route,
    target: 'utilization-tab-unmapped',
    layout: 'compact',
    illustration: 'click',
    titleKey: 'onboarding.utilization.unmappedTab.title',
    descriptionKey: 'onboarding.utilization.unmappedTab.description',
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'above',
    highlightTarget: true,
    allowTargetInteraction: true,
    advanceOnTargetClick: true,
    padding: 8,
    radius: 12,
    onEnter: pointUtilizationUnmappedTab,
  },
  {
    id: 'utilization-unmapped-intro',
    route,
    target: 'utilization-unmapped-table',
    layout: 'compact',
    illustration: 'link',
    titleKey: 'onboarding.utilization.unmappedIntro.title',
    descriptionKey: 'onboarding.utilization.unmappedIntro.description',
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    padding: 8,
    radius: 12,
    onEnter: showUtilizationUnmappedTab,
  },
  {
    id: 'utilization-setup-cta',
    route,
    target: 'utilization-unmapped-table',
    arrowTarget: 'utilization-setup',
    layout: 'compact',
    illustration: 'click',
    titleKey: 'onboarding.utilization.setupCta.title',
    descriptionKey: 'onboarding.utilization.setupCta.description',
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'above',
    highlightTarget: true,
    padding: 4,
    radius: 12,
    onEnter: showUtilizationSetupCta,
  },
  {
    id: 'utilization-complete',
    route,
    layout: 'complete',
    illustration: 'weightScale',
    titleKey: 'onboarding.utilization.complete.title',
    descriptionKey: 'onboarding.utilization.complete.description',
    values: getUtilizationTourValues,
    primaryLabelKey: 'onboarding.goToUtilization',
    secondaryLabelKey: 'onboarding.restart',
    secondaryAction: 'restart',
    onEnter: showUtilizationComplete,
  },
];

export const utilizationTour: OnboardingTour = {
  id: 'utilization',
  paths: ['/utilization'],
  steps: utilizationSteps.map((step, index) => ({ ...step, practice: 'utilization',
    onEnter: () => {
      setUtilizationExerciseSource(index >= utilizationSteps.findIndex(item => item.id === 'utilization-add-link'));
      return step.onEnter?.();
    },
  })),
};
